import {
	Button,
	Caption1,
	Dropdown,
	Input,
	makeStyles,
	Option,
	OptionOnSelectData,
	SelectionEvents,
	Skeleton,
	SkeletonItem,
	tokens
} from '@fluentui/react-components';
import { Add32Filled } from '@fluentui/react-icons';
import { createSelector } from '@reduxjs/toolkit';
import { useMemo, useState } from 'react';
import { useFetchGamesQuery } from '../Api/Games/gamesSlice';
import { VideoGameListItem } from '../Api/Games/types';
import { setGameSorting } from '../Application/applicationSlice';
import useDebouncer from '../Hooks/useDebouncer';
import { useAppDispatch, useAppSelector } from '../pgStore';
import Page from '../Shared/Page/Page';
import GameFormModal from './Form/GameFormModal';
import GameItem from './GameItem';
import { GameSortOptions } from './types';

const Games = () => {
	const styles = useStyles();
	const [searchText, setSearchText] = useState('');
	const [apiSearchText, setApiSearchText] = useState('');
	const sortOrder = useAppSelector(
		(state) => state.applicationSlice.gameSortOrder
	);
	const dispatch = useAppDispatch();

	const sortGames = useMemo(() => {
		const empty: VideoGameListItem[] = [];

		return createSelector(
			(games: VideoGameListItem[] | null | undefined) => games,
			(games) => {
				if (!games) {
					return empty;
				}

				const mutableArray = [...games];
				if (sortOrder === GameSortOptions.Recent) {
					return mutableArray.sort((left, right) =>
						(right.releaseDate ?? '').localeCompare(
							left.releaseDate ?? '',
							'en',
							{ sensitivity: 'base' }
						)
					);
				} else if (sortOrder === GameSortOptions.MostPopular) {
					return mutableArray.sort(
						(left, right) =>
							(right.totalReviews ?? 0) - (left.totalReviews ?? 0)
					);
				} else {
					return games;
				}
			}
		);
	}, [sortOrder]);

	const { sortedGames: games, isLoading } = useFetchGamesQuery(
		{ gameTitleStartsWith: apiSearchText },
		{
			selectFromResult: (result) => ({
				isLoading: result.isLoading,
				sortedGames: sortGames(result.data)
			})
		}
	);

	useDebouncer(
		() => {
			setApiSearchText(searchText);
		},
		[searchText],
		400
	);

	const [showAdd, setShowAdd] = useState(false);
	const currentUser = useAppSelector((state) => state.userSlice.username);
	const adminAddGame = () => {
		if (currentUser === 'SneakySquid') {
			setShowAdd(true);
		}
	};

	function PageActions() {
		return (
			<>
				<Input
					type='text'
					value={searchText}
					onChange={(event) => {
						setSearchText(event.target.value);
					}}
					placeholder='Search...'
					style={{ width: '100%', minWidth: '300px' }}
					appearance='underline'
				/>
				<Dropdown
					value={sortOrder}
					onOptionSelect={(
						_event: SelectionEvents,
						data: OptionOnSelectData
					) => {
						if (data.optionValue) {
							const enumValue =
								data.optionValue as GameSortOptions;
							dispatch(setGameSorting(enumValue));
						}
					}}
				>
					<Option value={GameSortOptions.Alphabetical}>
						Alphabetical
					</Option>
					<Option value={GameSortOptions.Recent}>Release Date</Option>
					<Option value={GameSortOptions.MostPopular}>
						Most Popular
					</Option>
				</Dropdown>
				{currentUser === 'SneakySquid' && (
					<Button
						title='Add New Game'
						appearance='primary'
						icon={<Add32Filled />}
						onClick={adminAddGame}
					></Button>
				)}
			</>
		);
	}

	return (
		<Page
			title='Games'
			actions={PageActions()}
		>
			<div className={styles.root}>
				{isLoading && (
					<>
						<Skeleton style={{ width: '100%' }}>
							<SkeletonItem
								shape='rectangle'
								size={36}
							/>
						</Skeleton>

						<div className={styles.container}>
							{Array.from(Array(20)).map((_item, index) => {
								return (
									<Skeleton key={index}>
										<div className={styles.skeletonCard}>
											<SkeletonItem
												shape='rectangle'
												size={40}
											/>
											<SkeletonItem
												shape='rectangle'
												size={128}
												style={{ height: '200px' }}
											/>
											<SkeletonItem
												shape='rectangle'
												size={40}
											/>
										</div>
									</Skeleton>
								);
							})}
						</div>
					</>
				)}

				{!isLoading && (
					<>
						<div className={styles.container}>
							{games.map((game, index) => (
								<GameItem
									key={`game-${String(index)}`}
									game={game}
								/>
							))}
						</div>
						{games.length < 1 && (
							<Caption1>No Games Found!</Caption1>
						)}
					</>
				)}

				{showAdd && (
					<GameFormModal
						showModal={showAdd}
						setShowModal={setShowAdd}
					/>
				)}
			</div>
		</Page>
	);
};

export default Games;

const useStyles = makeStyles({
	root: {
		display: 'flex',
		flexDirection: 'column',
		rowGap: '18px'
	},
	container: {
		display: 'flex',
		flexWrap: 'wrap',
		columnGap: '8px',
		rowGap: '8px',
		justifyContent: 'center'
	},
	skeletonCard: {
		backgroundColor: tokens.colorNeutralBackground2,
		width: '250px',
		height: '288px',
		display: 'flex',
		flexDirection: 'column',
		rowGap: '4px'
	}
});
